import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo
} from 'react'
import {
  decodeToken,
  getToken,
  saveToken,
  getUser,
  userDetails,
  removeUser,
  removeToken,
  isTokenExpired,
  notifySuccess,
  saveRefreshToken
} from '../utils'
import {
  secureSetItem,
  secureGetItem,
  secureRemoveItem
} from '../utils/secureStorage'
import tenantConfig from '../config/tenant'
import { LoginUserData } from '../pages/login/types'
import store from '../store/store'
import { authApi } from '../api/authApi'
import coverageTypeApi from '../api/coverageTypeApi'
import coverageBenefitApi from '../api/coverageBenefitApi'
import coverageRateApi from '../api/coverageRateApi'
import planApi from '../api/planApi'
import enumsApi from '../api/enumsApi'
import insuranceProductApi from '../api/insuranceProductApi'
import vehicleDetailsSearchApi from '../api/vehicleDetailsSearchApi'
import usersApi from '../api/userApi'
import quotationApi from '../api/quotationApi'
import applicationApi from '../api/applicationApi'
import dashboardApi from '../api/dashboardApi'
import profileApi from '../api/profileApi'
import payoutApi from '../api/payoutApi'

interface AuthContextType {
  isLoggedIn: boolean
  user: LoginUserData | null
  token: string | null
  role: string
  login: (token: string, refreshToken: string, user?: LoginUserData) => void
  logout: (showNotification?: boolean) => void
  refreshUser: () => void
  setUserData: (updates: Partial<LoginUserData>) => void
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

const getLoggedInKey = () => `${tenantConfig.storagePrefix}_isLoggedIn`

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [isLoggedIn, setIsLoggedIn] = useState(
    () => secureGetItem(getLoggedInKey()) === 'true'
  )
  const [token, setToken] = useState<string | null>(() => getToken())
  const [user, setUser] = useState<LoginUserData | null>(
    () => getUser() as LoginUserData | null
  )
  const [role, setRole] = useState<string>(() => {
    const storedUser = getUser() as LoginUserData | null
    return storedUser?.roles?.join(',') ?? ''
  })

  const resetAllApiCaches = useCallback(() => {
    store.dispatch(authApi.util.resetApiState())
    store.dispatch(coverageTypeApi.util.resetApiState())
    store.dispatch(coverageBenefitApi.util.resetApiState())
    store.dispatch(coverageRateApi.util.resetApiState())
    store.dispatch(planApi.util.resetApiState())
    store.dispatch(enumsApi.util.resetApiState())
    store.dispatch(insuranceProductApi.util.resetApiState())
    store.dispatch(vehicleDetailsSearchApi.util.resetApiState())
    store.dispatch(usersApi.util.resetApiState())
    store.dispatch(quotationApi.util.resetApiState())
    store.dispatch(applicationApi.util.resetApiState())
    store.dispatch(dashboardApi.util.resetApiState())
    store.dispatch(profileApi.util.resetApiState())
    store.dispatch(payoutApi.util.resetApiState())
  }, [])

  const handleLogout = useCallback(
    (showNotification = false) => {
      secureRemoveItem(getLoggedInKey())
      removeToken()
      removeUser()
      setToken(null)
      setUser(null)
      setRole('')
      setIsLoggedIn(false)
      resetAllApiCaches()
      localStorage.clear()
      sessionStorage.clear()
      if (showNotification) {
        notifySuccess('Logout successful')
      }
    },
    [resetAllApiCaches]
  )

  useEffect(() => {
    const storedToken = getToken()
    const storedUser = getUser() as LoginUserData | null
    const wasLoggedIn = secureGetItem(getLoggedInKey()) === 'true'
    if (storedToken && !isTokenExpired(storedToken)) {
      setToken(storedToken)
      setUser(storedUser)
      decodeToken(storedToken)
      setRole(storedUser?.roles?.join(',') ?? '')
      setIsLoggedIn(true)
    } else if (storedToken || storedUser || wasLoggedIn) {
      handleLogout()
    }
  }, [handleLogout])

  const handleLogin = useCallback(
    (newToken: string, refreshToken: string, userData?: LoginUserData) => {
      resetAllApiCaches()
      saveToken(newToken)
      saveRefreshToken(refreshToken)
      decodeToken(newToken)
      if (userData) {
        userDetails(userData)
      }
      setToken(newToken)
      setUser(userData ?? null)
      setRole(userData?.roles?.join(',') ?? '')
      secureSetItem(getLoggedInKey(), 'true')
      setIsLoggedIn(true)
    },
    [resetAllApiCaches]
  )

  const setUserData = useCallback((updates: Partial<LoginUserData>) => {
    setUser((prev) => {
      const merged = { ...(prev ?? {}), ...updates } as LoginUserData
      userDetails(merged)
      return merged
    })
  }, [])

  const refreshUser = useCallback(() => {
    const currentToken = getToken()
    const storedUser = getUser() as LoginUserData | null
    if (currentToken && !isTokenExpired(currentToken)) {
      const decoded = decodeToken(currentToken) as LoginUserData | null
      const mergedUser = decoded ? { ...decoded, ...storedUser } : storedUser
      setUser(mergedUser)
      setRole(mergedUser?.roles?.join(',') ?? '')
    } else {
      handleLogout()
    }
  }, [handleLogout])

  const value = useMemo(
    () => ({
      isLoggedIn,
      user,
      token,
      role,
      login: handleLogin,
      logout: handleLogout,
      refreshUser,
      setUserData
    }),
    [
      isLoggedIn,
      user,
      token,
      role,
      handleLogin,
      handleLogout,
      refreshUser,
      setUserData
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
